import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Skeleton,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudSyncOutlined,
  DatabaseOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";

import API_CONFIG from "../config/api";
import { useAuth } from "../context/AuthContext";
import "./BackupMonitoring.css";

const { Text, Title } = Typography;

const HEALTH_CONFIG = {
  healthy: {
    label: "À jour",
    color: "success",
    icon: <CheckCircleOutlined />,
    alertType: "success",
    message: "La dernière sauvegarde a été reçue et validée dans le délai prévu.",
  },
  running: {
    label: "En cours",
    color: "processing",
    icon: <CloudSyncOutlined spin />,
    alertType: "info",
    message: "Une sauvegarde est actuellement en cours sur le poste local.",
  },
  overdue: {
    label: "En retard",
    color: "warning",
    icon: <ClockCircleOutlined />,
    alertType: "warning",
    message: "Aucune sauvegarde réussie n'a été signalée dans le délai attendu.",
  },
  failed: {
    label: "Échec",
    color: "error",
    icon: <ExclamationCircleOutlined />,
    alertType: "error",
    message: "La dernière tentative de sauvegarde a échoué ou est restée bloquée.",
  },
  never: {
    label: "Aucune donnée",
    color: "default",
    icon: <ExclamationCircleOutlined />,
    alertType: "warning",
    message: "Aucun rapport n'a encore été reçu depuis le poste de sauvegarde.",
  },
};

const RUN_STATUS_CONFIG = {
  success: { label: "Réussie", color: "success" },
  failed: { label: "Échec", color: "error" },
  running: { label: "En cours", color: "processing" },
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const formatBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes === 0) return "0 o";
  const units = ["o", "Ko", "Mo", "Go", "To"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  return `${(bytes / 1024 ** unitIndex).toLocaleString("fr-FR", {
    maximumFractionDigits: unitIndex > 1 ? 2 : 0,
  })} ${units[unitIndex]}`;
};

const formatDuration = (secondsValue) => {
  const seconds = Number(secondsValue);
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes} min ${remainingSeconds} s`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
};

const StatusTag = ({ status }) => {
  const config = RUN_STATUS_CONFIG[status] || {
    label: status || "Inconnu",
    color: "default",
  };
  return <Tag color={config.color}>{config.label}</Tag>;
};

const BackupMonitoring = () => {
  const { token, user } = useAuth();
  const [statusData, setStatusData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadBackupData = useCallback(
    async ({ silent = false } = {}) => {
      if (!token || !user?.is_superadmin) return;

      if (silent) setRefreshing(true);
      else setLoading(true);

      try {
        const requestConfig = {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        };
        const [statusResponse, historyResponse] = await Promise.all([
          axios.get(
            API_CONFIG.url("/api/backup-monitoring/status"),
            requestConfig
          ),
          axios.get(
            API_CONFIG.url("/api/backup-monitoring/history?limit=30"),
            requestConfig
          ),
        ]);

        setStatusData(statusResponse.data?.data || null);
        setHistory(historyResponse.data?.data || []);
        setError("");
      } catch (requestError) {
        setError(
          requestError.response?.data?.message ||
            "Impossible de charger l'état des sauvegardes."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [token, user?.is_superadmin]
  );

  useEffect(() => {
    loadBackupData();
  }, [loadBackupData]);

  if (!user?.is_superadmin) {
    return <Navigate to="/accueil" replace />;
  }

  const health = statusData?.health || "never";
  const healthConfig = HEALTH_CONFIG[health] || HEALTH_CONFIG.never;
  const lastSuccess = statusData?.last_success;
  const latestAttempt = statusData?.latest_attempt;
  const summary = statusData?.summary_30d || {};

  const columns = [
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (value) => <StatusTag status={value} />,
    },
    {
      title: "Démarrage",
      dataIndex: "started_at",
      key: "started_at",
      width: 185,
      render: formatDate,
    },
    {
      title: "Durée",
      dataIndex: "duration_seconds",
      key: "duration_seconds",
      width: 115,
      render: formatDuration,
    },
    {
      title: "Archive",
      dataIndex: "archive_size_bytes",
      key: "archive_size_bytes",
      width: 115,
      render: formatBytes,
    },
    {
      title: "Objets",
      key: "objects",
      width: 130,
      render: (_, run) =>
        run.tables_count === null || run.tables_count === undefined
          ? "—"
          : `${run.tables_count} tables / ${run.views_count || 0} vues`,
    },
    {
      title: "Poste",
      dataIndex: "agent_id",
      key: "agent_id",
      width: 170,
    },
    {
      title: "Contrôle SHA-256",
      dataIndex: "sha256",
      key: "sha256",
      width: 170,
      render: (value) =>
        value ? (
          <Tooltip title={value}>
            <Text code>{`${value.slice(0, 12)}…`}</Text>
          </Tooltip>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <main className="backup-monitoring-page">
      <header className="backup-monitoring-header">
        <div>
          <Title level={2}>
            <DatabaseOutlined /> Sauvegardes MySQL
          </Title>
          <Text type="secondary">
            Contrôle à distance des sauvegardes locales de la base de production
          </Text>
        </div>
        <Button
          icon={<ReloadOutlined />}
          loading={refreshing}
          onClick={() => loadBackupData({ silent: true })}
        >
          Actualiser
        </Button>
      </header>

      {error && (
        <Alert
          className="backup-monitoring-alert"
          type="error"
          showIcon
          message="État indisponible"
          description={error}
          action={
            <Button size="small" onClick={() => loadBackupData({ silent: true })}>
              Réessayer
            </Button>
          }
        />
      )}

      {loading ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : !statusData ? (
        !error ? (
          <Card>
            <Empty description="État des sauvegardes indisponible" />
          </Card>
        ) : null
      ) : (
        <>
          <Alert
            className="backup-monitoring-alert"
            type={healthConfig.alertType}
            showIcon
            icon={healthConfig.icon}
            message={
              <span>
                État global : <strong>{healthConfig.label}</strong>
              </span>
            }
            description={
              latestAttempt?.status === "failed" && latestAttempt.error_message
                ? `${healthConfig.message} Détail : ${latestAttempt.error_message}`
                : healthConfig.message
            }
          />

          <Row gutter={[16, 16]} className="backup-stat-grid">
            <Col xs={24} sm={12} xl={6}>
              <Card className="backup-stat-card">
                <Statistic
                  title="Dernière réussite"
                  value={formatDate(lastSuccess?.completed_at)}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Card className="backup-stat-card">
                <Statistic
                  title="Prochaine sauvegarde planifiÃ©e"
                  value={formatDate(
                    statusData?.next_scheduled_at || statusData?.next_expected_at
                  )}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Card className="backup-stat-card">
                <Statistic
                  title="Taille de la dernière archive"
                  value={formatBytes(lastSuccess?.archive_size_bytes)}
                  prefix={<DatabaseOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} xl={6}>
              <Card className="backup-stat-card">
                <Statistic
                  title="Réussites sur 30 jours"
                  value={Number(summary.successful_attempts || 0)}
                  suffix={`/ ${Number(summary.total_attempts || 0)}`}
                  prefix={<CloudSyncOutlined />}
                />
              </Card>
            </Col>
          </Row>

          <Card
            className="backup-history-card"
            title="Historique des 30 dernières tentatives"
            extra={
              statusData?.checked_at ? (
                <Text type="secondary">
                  Vérifié le {formatDate(statusData.checked_at)}
                </Text>
              ) : null
            }
          >
            {history.length === 0 ? (
              <Empty description="Aucun rapport de sauvegarde reçu" />
            ) : (
              <>
                <Table
                  className="backup-history-table"
                  rowKey="run_id"
                  columns={columns}
                  dataSource={history}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  scroll={{ x: 1000 }}
                />

                <div className="backup-history-mobile">
                  {history.map((run) => (
                    <article className="backup-run-card" key={run.run_id}>
                      <div className="backup-run-card-header">
                        <StatusTag status={run.status} />
                        <Text type="secondary">{formatDate(run.started_at)}</Text>
                      </div>
                      <dl>
                        <div>
                          <dt>Durée</dt>
                          <dd>{formatDuration(run.duration_seconds)}</dd>
                        </div>
                        <div>
                          <dt>Archive</dt>
                          <dd>{formatBytes(run.archive_size_bytes)}</dd>
                        </div>
                        <div>
                          <dt>Poste</dt>
                          <dd>{run.agent_id}</dd>
                        </div>
                        <div>
                          <dt>Tables / vues</dt>
                          <dd>
                            {run.tables_count ?? "—"} / {run.views_count ?? "—"}
                          </dd>
                        </div>
                      </dl>
                      {run.error_message && (
                        <Alert type="error" showIcon message={run.error_message} />
                      )}
                    </article>
                  ))}
                </div>
              </>
            )}
          </Card>
        </>
      )}
    </main>
  );
};

export default BackupMonitoring;
